import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd } from "../../utils/jsonld.js";
import EventsMap from "../../components/events/EventsMap.jsx";
import { getEventsForLang } from "../../utils/content.js";
import { eventCategoryLabel } from "../../utils/eventCategories.js";

function formatEventDate(dateStr, lang) {
  return new Date(dateStr).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Own events (no externalOrga flag) attribute to Tales&Legends itself — everything else names the partner orga. */
function orgaLabel(f) {
  return f.externalOrga ? f.orgaName || "?" : "Tales&Legends";
}

/** Pill-shaped "Label ▾" trigger that opens a dropdown list of options — matches the Website.dc.html events filter bar (the "Tags ▾"/"Orga ▾" controls), not the old always-expanded pill row. */
function FilterDropdown({ label, allLabel, options, active, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function handlePointerDown(event) {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    }
    function handleKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={label}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "13px",
          color: active ? "var(--color-cream)" : "var(--color-cream-soft)",
          background: "none",
          border: `1px solid ${active ? "var(--color-gold)" : "rgba(254,250,239,0.18)"}`,
          padding: "8px 16px",
          borderRadius: "999px",
          cursor: "pointer",
          font: "inherit",
        }}
      >
        {active ?? label}
        <span style={{ color: "rgba(254,250,239,0.4)", fontSize: "10px" }}>▾</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, paddingTop: "8px", minWidth: "200px", zIndex: 30 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              background: "var(--color-bg-raised)",
              border: "1px solid rgba(254,250,239,0.12)",
              borderRadius: "12px",
              padding: "8px",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
              maxHeight: "260px",
              overflowY: "auto",
            }}
          >
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="tl-nav-dropdown-item"
              style={{
                textAlign: "left",
                padding: "10px 14px",
                borderRadius: "8px",
                background: "none",
                border: "none",
                cursor: "pointer",
                font: "inherit",
                fontSize: "14px",
                color: active === null ? "var(--color-gold)" : "var(--color-cream)",
              }}
            >
              {allLabel ?? t("events.allTags")}
            </button>
            {options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                aria-pressed={active === option}
                className="tl-nav-dropdown-item"
                style={{
                  textAlign: "left",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  font: "inherit",
                  fontSize: "14px",
                  color: active === option ? "var(--color-gold)" : "var(--color-cream)",
                }}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function EventList({ lang }) {
  const { t } = useTranslation();
  const [view, setView] = useState("list");
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [activeOrga, setActiveOrga] = useState(null);
  const events = getEventsForLang(lang).sort((a, b) => a.frontmatter.date.localeCompare(b.frontmatter.date));
  const allCategories = [...new Set(events.map((e) => e.frontmatter.category).filter(Boolean))];
  const allTags = [...new Set(events.flatMap((e) => e.frontmatter.tags ?? []))].sort();
  const allOrgas = [...new Set(events.map((e) => orgaLabel(e.frontmatter)))].sort(
    (a, b) => (b === "Tales&Legends") - (a === "Tales&Legends") || a.localeCompare(b)
  );
  const visibleEvents = events
    .filter((e) => !activeCategory || e.frontmatter.category === activeCategory)
    .filter((e) => !activeTag || (e.frontmatter.tags ?? []).includes(activeTag))
    .filter((e) => !activeOrga || orgaLabel(e.frontmatter) === activeOrga);
  // Guards the actual Maps API load, not just the toggle button — a
  // billable "map load" must never fire for an empty map (see
  // konzepte/GOOGLE_MAPS_SETUP.md).
  const mappableEvents = visibleEvents.filter((e) => e.frontmatter.coordinates?.length === 2);

  return (
    <>
      <SEOHead lang={lang} path="/events" title={t("nav.events")} description={t("events.description")} />
      <JsonLd
        data={buildBreadcrumbJsonLd(
          [
            { name: t("nav.home"), path: "/" },
            { name: t("nav.events"), path: "/events" },
          ],
          lang
        )}
      />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "32px", padding: "64px 40px 96px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px", maxWidth: "52ch" }}>
            <p className="tl-eyebrow" style={{ margin: 0 }}>
              {t("events.eyebrow")}
            </p>
            <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "44px", lineHeight: 1.1, color: "var(--color-cream)", margin: 0 }}>
              {t("events.title")}
            </h1>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            {allCategories.length > 0 && (
              <div role="group" aria-label={t("events.filterByCategory")} style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => setActiveCategory(null)}
                  className={`tl-pill ${activeCategory === null ? "tl-pill-primary" : "tl-pill-outline"}`}
                  style={{ padding: "9px 18px", fontSize: "13px" }}
                >
                  {t("events.allTags")}
                </button>
                {allCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setActiveCategory(category)}
                    aria-pressed={activeCategory === category}
                    className={`tl-pill ${activeCategory === category ? "tl-pill-primary" : "tl-pill-outline"}`}
                    style={{ padding: "9px 18px", fontSize: "13px" }}
                  >
                    {eventCategoryLabel(category, lang)}
                  </button>
                ))}
                <span style={{ width: "1px", height: "22px", background: "rgba(254,250,239,0.14)", margin: "0 6px" }} />
              </div>
            )}
            {allOrgas.length > 1 && (
              <FilterDropdown label={t("events.filterByOrga")} options={allOrgas} active={activeOrga} onChange={setActiveOrga} />
            )}
            {allTags.length > 0 && (
              <FilterDropdown label={t("events.filterByTag")} options={allTags} active={activeTag} onChange={setActiveTag} />
            )}
            </div>

            <button
              type="button"
              onClick={() => setView((v) => (v === "list" ? "map" : "list"))}
              aria-label={t("events.viewToggleLabel")}
              className="tl-pill tl-pill-outline"
              style={{ padding: "10px 18px", fontSize: "13px", flex: "none" }}
            >
              {view === "list" ? t("events.viewMap") : t("events.viewList")}
            </button>
          </div>

          {events.length === 0 && <p style={{ color: "var(--color-cream-soft)" }}>{t("events.noEvents")}</p>}
          {events.length > 0 && visibleEvents.length === 0 && (
            <p style={{ color: "var(--color-cream-soft)" }}>{t("events.noFilteredEvents")}</p>
          )}

          {view === "map" && visibleEvents.length > 0 && mappableEvents.length > 0 && <EventsMap events={mappableEvents} lang={lang} />}
          {view === "map" && visibleEvents.length > 0 && mappableEvents.length === 0 && (
            <p style={{ color: "var(--color-cream-soft)" }}>{t("events.mapNoLocations")}</p>
          )}

          {view === "list" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "24px" }}>
            {visibleEvents.map((event) => {
              const f = event.frontmatter;
              return (
                <Link
                  key={f.slug}
                  to={`/${lang}/events/${f.slug}`}
                  className="tl-card-light tl-hover-lift"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <div className="tl-diagonal-light" style={{ height: "170px" }} />
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "24px", flex: 1 }}>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                      <span className="tl-badge">{formatEventDate(f.date, lang)}</span>
                      <span
                        style={{
                          fontSize: "11px",
                          color: f.externalOrga ? "rgba(10,9,7,0.6)" : "#8a6520",
                          border: `1px solid ${f.externalOrga ? "rgba(10,9,7,0.18)" : "rgba(206,149,50,0.5)"}`,
                          padding: "3px 9px",
                          borderRadius: "999px",
                        }}
                      >
                        {orgaLabel(f)}
                      </span>
                      <span style={{ fontSize: "12px", color: "rgba(10,9,7,0.7)" }}>{f.location}</span>
                    </div>
                    <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", lineHeight: 1.22, color: "var(--color-ink)" }}>
                      {f.title}
                    </span>
                    {f.tags?.length > 0 && (
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {f.tags.map((tag) => (
                          <span key={tag} style={{ fontSize: "11px", color: "rgba(10,9,7,0.6)", border: "1px solid rgba(10,9,7,0.18)", padding: "3px 9px", borderRadius: "999px" }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto" }}>
                      <span style={{ fontSize: "13px", color: "rgba(10,9,7,0.7)" }}>
                        {t("events.priceFrom")} {f.price} €
                      </span>
                      <span className="tl-pill tl-pill-dark" style={{ fontSize: "13px" }}>
                        {t("events.detailsCta")}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          )}
        </div>
      </div>
    </>
  );
}
